import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2 } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import advisorkhojLogo from '@/assets/email-logo.webp';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { PublicShell } from '@/pages/public/PublicShell';
import { acceptPublicTc, getPublicTc } from '@/services/tc.service';

const formatDateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

const PublicTcPage = () => {
  const { token = '' } = useParams();
  const queryClient = useQueryClient();
  const [agreed, setAgreed] = useState(false);

  const tcQuery = useQuery({
    queryKey: ['public-tc', token],
    queryFn: () => getPublicTc(token),
    retry: false,
  });

  const acceptMutation = useMutation({
    mutationFn: () => acceptPublicTc(token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['public-tc', token] });
    },
  });

  if (tcQuery.isLoading) {
    return (
      <PublicShell brand="AdvisorKhoj" badge="Terms &amp; Conditions">
        <div className="rounded-xl border border-border bg-card p-6 text-sm text-muted-foreground shadow-sm">
          Loading the document…
        </div>
      </PublicShell>
    );
  }

  if (tcQuery.isError || !tcQuery.data) {
    return (
      <PublicShell brand="AdvisorKhoj" badge="Terms &amp; Conditions">
        <div className="grid place-items-center gap-3 rounded-xl border border-border bg-card p-8 text-center shadow-sm">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-danger/15 text-danger">
            <AlertCircle className="h-6 w-6" />
          </div>
          <h2 className="text-lg font-bold">Link not found</h2>
          <p className="text-sm text-muted-foreground">
            This Terms &amp; Conditions link is invalid or has expired. Please contact your
            relationship manager.
          </p>
        </div>
      </PublicShell>
    );
  }

  const doc = tcQuery.data;
  const isAccepted = doc.status === 'accepted';

  return (
    <PublicShell brand="AdvisorKhoj" badge="Terms &amp; Conditions" maxWidth="2xl">
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex justify-center">
          <img src={advisorkhojLogo} alt="AdvisorKhoj" className="h-10 object-contain" />
        </div>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold">{doc.title}</h2>
          </div>
          {isAccepted && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-600">
              <CheckCircle2 className="h-4 w-4" />
              Accepted
            </span>
          )}
        </div>

        <div
          className="tc-doc mt-4 max-h-[62vh] overflow-y-auto rounded-lg border border-border bg-background p-5"
          // eslint-disable-next-line react/no-danger -- trusted, server-authored static T&C content
          dangerouslySetInnerHTML={{ __html: doc.html }}
        />

        {isAccepted ? (
          <div className="mt-5 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-4 text-sm">
            <p className="font-semibold text-emerald-700">
              Thank you. Your acceptance has been recorded.
            </p>
            {doc.acceptedAt && (
              <p className="mt-1 text-muted-foreground">
                Accepted on {formatDateTime(doc.acceptedAt)}. You may close this page.
              </p>
            )}
          </div>
        ) : (
          <>
            {/* Checkbox and Submit sit in one row so the button is always
                visible with the acceptance statement - never hidden below the
                scrolled document (Fix 2). Stacks on phones, side-by-side from sm. */}
            <div className="mt-5 flex flex-col gap-3 rounded-lg border border-border p-4 sm:flex-row sm:items-center sm:justify-between">
              <label className="flex cursor-pointer items-start gap-3">
                <Checkbox
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-sm">
                  I have read, understood, and accept the Terms &amp; Conditions set out above.
                </span>
              </label>
              <Button
                className="w-full shrink-0 sm:w-auto"
                disabled={!agreed || acceptMutation.isPending}
                onClick={() => acceptMutation.mutate()}
              >
                {acceptMutation.isPending ? 'Submitting…' : 'Submit acceptance'}
              </Button>
            </div>
            {acceptMutation.isError && (
              <p className="mt-2 text-sm text-danger">
                Could not record your acceptance. Please try again.
              </p>
            )}
          </>
        )}
      </div>
    </PublicShell>
  );
};

export default PublicTcPage;
